/**
 * OtpGateModal — Dual-mode Firebase Phone OTP verification modal.
 *
 * MODES:
 *   • "free_sample" (default) — Shows first-name field. Creates a new
 *     FoodPhotos account if this phone has never signed in before.
 *     Used for anonymous free-sample download flow from HomePage.
 *
 *   • "existing_user_signin" — Hides first-name field. After Firebase
 *     verifies OTP ownership, the backend enforces a strict DB lookup
 *     and REJECTS unknown numbers with an inline "no account found"
 *     message. Never silently creates a user in this mode.
 *
 * SECURITY NOTE (Prevents phone number enumeration):
 *   The account-existence check is ONLY performed AFTER the caller has
 *   proven ownership of the phone number via Firebase-verified OTP.
 *   There is no public account-existence endpoint.
 *
 * On success:
 *   1. Stores JWT (via AuthContext.login)
 *   2. Persists pending action (search + previewImageId) via onBeforeVerify
 *   3. Navigates to `returnTo` (if provided) or `/dashboard`
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { authAPI } from "@/lib/api";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, Lock, X } from "lucide-react";
import { trackMetaEvent } from "@/lib/meta";
import { trackInternalEvent } from "@/lib/analytics";
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from "@/lib/firebase";

export const PENDING_DOWNLOAD_KEY = "spp_pending_download";

const COPY = {
  free_sample: {
    phoneTitle: "Unlock Your Free Sample Downloads",
    phoneSubtitle:
      "Verify your mobile number to unlock your 2 free sample downloads. It takes less than 10 seconds.",
    footer: "Your free sample download will continue after verification.",
    successToast: "Verified! Continuing to your download…",
  },
  existing_user_signin: {
    phoneTitle: "Welcome Back to FoodPhotos",
    phoneSubtitle:
      "Verify your registered mobile number to restore your Studio Pass access and continue from where you left off. It takes less than 10 seconds.",
    footer:
      "\uD83D\uDD12 Your existing plan, download access and any remaining free samples will be restored after verification.",
    successToast: "Signed in successfully.",
  },
};

export default function OtpGateModal({ open, onClose, pendingAction, mode = "free_sample", returnTo = null }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const phoneInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const recaptchaRef = useRef(null);
  const confirmationRef = useRef(null);

  const isSigninMode = mode === "existing_user_signin";
  const copy = COPY[isSigninMode ? "existing_user_signin" : "free_sample"];

  const [step, setStep] = useState("phone");
  const [firstName, setFirstName] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [firstNameError, setFirstNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [signinError, setSigninError] = useState("");

  const formatPhone = (val) => {
    let digits = val.replace(/\D/g, "");
    if (digits.length > 10 && digits.startsWith("91")) digits = digits.slice(2);
    return digits.slice(0, 10);
  };

  const setupRecaptcha = useCallback(async () => {
    if (recaptchaRef.current) {
      try { recaptchaRef.current.clear(); } catch (_) { /* noop */ }
      recaptchaRef.current = null;
    }
    const container = document.getElementById("otp-gate-recaptcha");
    if (container) container.innerHTML = "";
    const verifier = new RecaptchaVerifier(auth, "otp-gate-recaptcha", {
      size: "normal",
      callback: () => {},
      "expired-callback": () => {
        recaptchaRef.current = null;
        toast.error("reCAPTCHA expired. Please try again.");
      },
    });
    await verifier.render();
    recaptchaRef.current = verifier;
  }, []);

  // Reset state when opened / closed
  useEffect(() => {
    if (!open) {
      setStep("phone");
      setOtp("");
      setFirstName("");
      setPhone("");
      setFirstNameError("");
      setPhoneError("");
      setSigninError("");
      confirmationRef.current = null;
      if (recaptchaRef.current) {
        try { recaptchaRef.current.clear(); } catch (_) { /* noop */ }
        recaptchaRef.current = null;
      }
    }
  }, [open]);

  // Render recaptcha when on phone step
  useEffect(() => {
    if (!open) return;
    if (step === "phone") {
      const timer = setTimeout(() => { setupRecaptcha(); }, 300);
      return () => clearTimeout(timer);
    }
  }, [open, step, setupRecaptcha]);

  // Track modal open
  useEffect(() => {
    if (open) {
      trackInternalEvent("OtpGateOpened", {
        metadata: {
          source: pendingAction?.source || (isSigninMode ? "existing_user_signin" : "download_click"),
          mode,
          image_id: pendingAction?.previewImageId || "",
          search: pendingAction?.search || "",
        },
      });
    }
  }, [open, pendingAction, mode, isSigninMode]);

  // Cooldown timer
  useEffect(() => {
    if (step === "otp") setResendCooldown(30);
  }, [step]);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const persistPending = () => {
    if (!pendingAction) return;
    try {
      sessionStorage.setItem(PENDING_DOWNLOAD_KEY, JSON.stringify({
        ...pendingAction,
        saved_at: Date.now(),
      }));
    } catch (_) { /* noop */ }
  };

  const handleRequestOTP = async () => {
    const trimmedName = firstName.trim();
    let hasError = false;
    if (!isSigninMode) {
      if (trimmedName.length < 2) { setFirstNameError("Enter a valid first name"); hasError = true; }
      else setFirstNameError("");
    }
    if (phone.length !== 10) { setPhoneError("Enter a valid 10-digit mobile number"); hasError = true; }
    else setPhoneError("");
    if (hasError) return;

    setSendingOTP(true);
    setSigninError("");
    try {
      if (!recaptchaRef.current) await setupRecaptcha();
      const confirmation = await signInWithPhoneNumber(auth, `+91${phone}`, recaptchaRef.current);
      confirmationRef.current = confirmation;
      authAPI.logOtpRequest(`+91${phone}`, trimmedName).catch(() => {});
      trackInternalEvent("SignupStarted", {
        metadata: {
          phone: `+91${phone}`,
          first_name: trimmedName,
          source: isSigninMode ? "existing_user_signin" : "otp_gate",
          mode,
        },
      });
      toast.success("OTP sent to your phone");
      setStep("otp");
    } catch (err) {
      if (recaptchaRef.current) { try { recaptchaRef.current.clear(); } catch (_) { /* noop */ } recaptchaRef.current = null; }
      const code = err?.code || ""; const msg = err?.message || "";
      if (code === "auth/too-many-requests") toast.error("Too many attempts. Please try again later.");
      else if (code === "auth/invalid-phone-number") toast.error("Invalid phone number. Please check and try again.");
      else if (code === "auth/operation-not-allowed") toast.error("Phone sign-in is not enabled. Please contact support.");
      else if (code === "auth/captcha-check-failed" || msg.includes("reCAPTCHA")) toast.error("reCAPTCHA verification failed. Please refresh and try again.");
      else if (code === "auth/network-request-failed") toast.error("Network error. Please check your connection and try again.");
      else toast.error(`OTP failed: ${code || msg || "Unknown error. Please try again."}`);
    } finally {
      setSendingOTP(false);
    }
  };

  const handleVerifyOTP = async (otpValue) => {
    const code = otpValue || otp;
    if (code.length !== 6 || !confirmationRef.current) return;
    setVerifying(true);
    setSigninError("");
    try {
      // Step 1: Verify OTP ownership via Firebase (this proves the caller
      // controls the phone number BEFORE the backend performs any account lookup).
      const result = await confirmationRef.current.confirm(code);
      const idToken = await result.user.getIdToken();

      // Step 2: Ask backend to verify + look up (or create, only in free_sample mode)
      const res = await authAPI.firebaseVerify(idToken, firstName.trim(), mode);
      const { access_token, refresh_token, user, meta_event_id, meta_event_name } = res.data;

      // Persist pending action BEFORE navigating so DashboardPage can restore it
      persistPending();

      login(access_token, refresh_token, user);
      if (meta_event_id && meta_event_name) trackMetaEvent(meta_event_name, meta_event_id);
      trackInternalEvent("SignupCompleted", {
        user_id: user?.user_id,
        meta_event_id: meta_event_id || "",
        metadata: {
          phone: user?.phone,
          is_new: !!user?.is_new_user,
          source: isSigninMode ? "existing_user_signin" : "otp_gate",
          mode,
        },
      });
      trackInternalEvent("OtpGateVerified", {
        user_id: user?.user_id,
        metadata: {
          image_id: pendingAction?.previewImageId || "",
          search: pendingAction?.search || "",
          mode,
        },
      });
      toast.success(copy.successToast);
      navigate(returnTo || "/dashboard", { replace: true });
    } catch (err) {
      const fbCode = err?.code || "";
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      // Backend explicitly refused because no FoodPhotos account exists
      // for this phone (only surfaced AFTER OTP ownership is verified).
      if (isSigninMode && status === 404 && detail) {
        setSigninError(detail);
        setStep("phone");
        setOtp("");
        return;
      }

      if (fbCode === "auth/invalid-verification-code") toast.error("Invalid OTP. Please check and try again.");
      else if (fbCode === "auth/code-expired") toast.error("OTP expired. Please request a new one.");
      else toast.error(detail || "Verification failed. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleOTPChange = (value) => {
    setOtp(value);
    if (value.length === 6) handleVerifyOTP(value);
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    try {
      await setupRecaptcha();
      const confirmation = await signInWithPhoneNumber(auth, `+91${phone}`, recaptchaRef.current);
      confirmationRef.current = confirmation;
      toast.success("OTP sent again");
      setResendCooldown(30);
    } catch (err) {
      if (recaptchaRef.current) { try { recaptchaRef.current.clear(); } catch (_) { /* noop */ } recaptchaRef.current = null; }
      toast.error("Failed to resend OTP. Please refresh and try again.");
    } finally {
      setResending(false);
    }
  };

  if (!open) return null;

  const submitDisabled =
    (isSigninMode ? false : firstName.trim().length < 2) || phone.length !== 10 || sendingOTP;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4" data-testid="otp-gate-modal" data-mode={mode}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[440px] rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundColor: "#111827" }}>
        <button
          data-testid="otp-gate-close-btn"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-7 pt-8">
          {step === "phone" ? (
            <>
              <h2 className="text-xl font-semibold text-white mb-1.5 tracking-tight" style={{ fontFamily: "Inter, system-ui, sans-serif" }} data-testid="otp-gate-headline">
                {copy.phoneTitle}
              </h2>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed" data-testid="otp-gate-subheadline">
                {copy.phoneSubtitle}
              </p>

              {signinError && (
                <div
                  className="mb-5 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-[13px] text-red-200 leading-relaxed"
                  data-testid="otp-gate-signin-error"
                  role="alert"
                >
                  {signinError}
                </div>
              )}

              <div className="space-y-4">
                {!isSigninMode && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">First name</label>
                    <input
                      ref={nameInputRef}
                      data-testid="otp-gate-first-name-input"
                      type="text"
                      placeholder="Enter your first name"
                      value={firstName}
                      onChange={(e) => { setFirstName(e.target.value); if (firstNameError) setFirstNameError(""); }}
                      onBlur={() => { if (firstName.trim().length > 0 && firstName.trim().length < 2) setFirstNameError("Enter a valid first name"); }}
                      onKeyDown={(e) => e.key === "Enter" && phoneInputRef.current?.focus()}
                      className="w-full h-11 px-3 rounded-lg border border-slate-600 bg-slate-800/60 text-white text-base placeholder:text-slate-500 outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 transition-colors"
                      autoFocus
                      autoComplete="given-name"
                    />
                    {firstNameError && <p className="text-xs text-red-400 mt-1.5" data-testid="otp-gate-first-name-error">{firstNameError}</p>}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {isSigninMode ? "Registered mobile number" : "Phone number"}
                  </label>
                  <div className="flex gap-2">
                    <div className="flex items-center justify-center px-3 h-11 rounded-lg border border-slate-600 bg-slate-800/60 text-sm font-medium text-slate-300 select-none">+91</div>
                    <input
                      data-testid="otp-gate-phone-input"
                      ref={phoneInputRef}
                      type="tel"
                      inputMode="numeric"
                      placeholder="98765 43210"
                      value={phone}
                      onChange={(e) => { setPhone(formatPhone(e.target.value)); if (phoneError) setPhoneError(""); if (signinError) setSigninError(""); }}
                      onBlur={() => { if (phone.length > 0 && phone.length !== 10) setPhoneError("Enter a valid 10-digit mobile number"); }}
                      onKeyDown={(e) => e.key === "Enter" && handleRequestOTP()}
                      className="flex-1 h-11 px-3 rounded-lg border border-slate-600 bg-slate-800/60 text-white text-base placeholder:text-slate-500 outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 transition-colors"
                      autoComplete="tel"
                      autoFocus={isSigninMode}
                    />
                  </div>
                  {phoneError ? (
                    <p className="text-xs text-red-400 mt-1.5" data-testid="otp-gate-phone-error">{phoneError}</p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1.5">Enter your 10-digit mobile number</p>
                  )}
                </div>

                <div id="otp-gate-recaptcha" className="flex justify-center mb-1" />

                <button
                  data-testid="otp-gate-submit-phone-btn"
                  onClick={handleRequestOTP}
                  disabled={submitDisabled}
                  className="w-full h-11 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-1.5 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: !submitDisabled
                      ? "linear-gradient(135deg, #F59E0B, #F97316)"
                      : "linear-gradient(135deg, #78716c, #57534e)",
                  }}
                >
                  {sendingOTP ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Send OTP <ArrowRight className="h-4 w-4" /></>)}
                </button>

                <p className="text-[11px] text-slate-400 text-center pt-2 flex items-center justify-center gap-1.5">
                  {!isSigninMode && <Lock className="h-3 w-3" />}
                  {copy.footer}
                </p>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-white mb-1.5 tracking-tight" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
                Enter verification code
              </h2>
              <p className="text-sm text-slate-400 mb-6">
                We sent a 6-digit code to +91 {phone.slice(0, 5)} {phone.slice(5)}
              </p>

              <div className="space-y-5">
                <div className="flex justify-center otp-input-group">
                  <InputOTP
                    data-testid="otp-gate-otp-input"
                    maxLength={6}
                    value={otp}
                    onChange={handleOTPChange}
                    disabled={verifying}
                  >
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg border-slate-600 bg-slate-800/60 text-white" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <button
                  data-testid="otp-gate-verify-btn"
                  onClick={() => handleVerifyOTP()}
                  disabled={otp.length !== 6 || verifying}
                  className="w-full h-11 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-1.5 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: otp.length === 6 && !verifying
                      ? "linear-gradient(135deg, #F59E0B, #F97316)"
                      : "linear-gradient(135deg, #78716c, #57534e)",
                  }}
                >
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Continue"}
                </button>

                <button
                  data-testid="otp-gate-resend-btn"
                  onClick={handleResendOTP}
                  disabled={resendCooldown > 0 || resending}
                  className="text-sm text-center w-full disabled:cursor-not-allowed transition-colors"
                  style={{ color: resendCooldown > 0 ? "#64748b" : "#f59e0b" }}
                >
                  {resending ? "Sending..." : resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : "Resend OTP"}
                </button>

                <button
                  data-testid="otp-gate-back-btn"
                  onClick={() => { setStep("phone"); setOtp(""); }}
                  className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors mx-auto"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Change phone number
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
